import React, { useEffect, useState } from "react";
import {
  MinusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Button, Form, Image, Input, InputNumber, Switch, Tooltip } from "antd";
import IconSelector from "./IconSelector";
import { useTranslation } from "react-i18next";

const RewardForm = ({
  form,
  selectedLayout,
  hasWelcomeReward,
  setRewardsAssets,
  iconsTheme,
}) => {
  const { t } = useTranslation();
  const [allowedValues, setAllowedValues] = useState([]);

  const getFirstRewardValue = () => {
    if (selectedLayout === "layout1") return 5;
    if (selectedLayout === "layout2") return 8;
    return 1;
  };

  const getAllowedValues = () => {
    if (selectedLayout === "layout1") return [1, 2, 3];
    if (selectedLayout === "layout2") return [1, 2, 3, 4, 5, 6];
    return [];
  };

  const updateAllowedAndFirstReward = () => {
    const rewards = form.getFieldValue("rewards") || [];
    const firstValue = getFirstRewardValue();
    const updatedRewards = [...(rewards.length ? rewards : [{}])];
    const currentFirst = updatedRewards[0] || {};

    if (currentFirst.qualifyingPurchases !== firstValue) {
      updatedRewards[0] = {
        ...currentFirst,
        qualifyingPurchases: firstValue,
      };
      form.setFieldsValue({ rewards: updatedRewards });
    }

    setAllowedValues(getAllowedValues());
  };

  useEffect(() => {
    updateAllowedAndFirstReward();
  }, [selectedLayout, hasWelcomeReward]);

  return (
    <div className="rewards-form rewards-form-section">
      <div className="rewards-form-title">
        <h4>{t("rewards")}</h4>
        <Tooltip
          title={
            <Image
              src="/assets/images/stampcard/stamp-hint.png"
              alt={t("hint")}
              style={{ maxWidth: 300 }}
            />
          }
        >
          <QuestionCircleOutlined className="tooltip-icon" />
        </Tooltip>
      </div>

      <div
        className="rewards-form"
        name="dynamic_form_rewards"
        autoComplete="off"
      >
        <Form.List name="rewards">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => {
                const isFirst = index === 0;
                const firstValue = getFirstRewardValue();

                return (
                  <div key={key}>
                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "title"]}
                        label={t("title")}
                        rules={[{ required: true, message: t("missingTitle") }]}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item label={t("icons")} required>
                        <IconSelector
                          theme={iconsTheme}
                          onChange={({ target, inking }) => {
                            const rewards = form.getFieldValue("rewards") || [];
                            const updated = [...rewards];
                            updated[name] = {
                              ...updated[name],
                              inkingIcon: inking,
                              targetIcon: target,
                            };
                            form.setFieldsValue({ rewards: updated });
                            setRewardsAssets(updated);
                          }}
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "inkingIcon"]}
                        style={{ display: "none" }}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "targetIcon"]}
                        style={{ display: "none" }}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "qualifyingPurchases"]}
                        label={t("qualifyingPurchases")}
                        rules={[
                          {
                            required: true,
                            message: t("enterRequiredPurchases"),
                          },
                          {
                            validator: (_, value) => {
                              if (isFirst) return Promise.resolve();
                              if (!value)
                                return Promise.reject(
                                  new Error(t("enterValue"))
                                );
                              if (!allowedValues.includes(value))
                                return Promise.reject(
                                  new Error(
                                    t("mustBeOneOf", {
                                      values: allowedValues.join(", "),
                                    })
                                  )
                                );

                              const first = getFirstRewardValue();
                              if ([first, first - 1, first + 1].includes(value))
                                return Promise.reject(
                                  new Error(
                                    t("cannotBeAdjacent", {
                                      first,
                                      firstMinus: first - 1,
                                      firstPlus: first + 1,
                                    })
                                  )
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          readOnly={isFirst}
                          style={isFirst ? { backgroundColor: "#f5f5f5" } : {}}
                          min={Math.min(...allowedValues)}
                          max={Math.max(...allowedValues)}
                          placeholder={
                            isFirst
                              ? `${firstValue}`
                              : t("allowedValues", {
                                  values: allowedValues.join(", "),
                                })
                          }
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "confirmationTitle"]}
                        label={t("confirmationTitle")}
                        rules={[
                          {
                            required: true,
                            message: t("missingConfirmationTitle"),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "confirmationMessage"]}
                        label={t("confirmationMessage")}
                        rules={[
                          {
                            required: true,
                            message: t("missingConfirmationMessage"),
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "deferrable"]}
                        label={t("deferrable")}
                        valuePropName="checked"
                      >
                        <Switch
                          checkedChildren={t("yes")}
                          unCheckedChildren={t("no")}
                        />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "voucherDurationDays"]}
                        label={t("voucherDurationDays")}
                        rules={[
                          {
                            required: true,
                            message: t("enterVoucherDuration"),
                          },
                        ]}
                      >
                        <InputNumber min={1} max={99} step={1} precision={0} />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "description"]}
                        label={t("description")}
                        rules={[
                          { required: true, message: t("missingDescription") },
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item
                        {...restField}
                        name={[name, "terms"]}
                        label={t("terms")}
                        rules={[{ required: true, message: t("missingTerms") }]}
                      >
                        <Input />
                      </Form.Item>
                    </div>

                    <div className="form-row margin-y">
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => remove(name)}
                          block
                          icon={<MinusCircleOutlined />}
                          disabled={fields.length === 1}
                        >
                          {t("remove")}
                        </Button>
                      </Form.Item>
                    </div>
                  </div>
                );
              })}

              {fields.length < 2 && (
                <div className="form-row margin-y">
                  <Button
                    type="primary"
                    onClick={() => {
                      const newReward = {
                        qualifyingPurchases:
                          allowedValues.find(
                            (val) =>
                              val !== getFirstRewardValue() &&
                              ![
                                getFirstRewardValue() - 1,
                                getFirstRewardValue() + 1,
                              ].includes(val)
                          ) || allowedValues[0],
                        title: "",
                        confirmationTitle: "",
                        confirmationMessage: "",
                        deferrable: false,
                        voucherDurationDays: 1,
                        description: "",
                        terms: "",
                      };
                      add(newReward);
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    {t("addReward")}
                  </Button>
                </div>
              )}

              {fields.length >= 2 && (
                <div className="form-row margin-y">
                  <p style={{ textAlign: "center", color: "#888" }}>
                    {t("maxTwoRewards")}
                  </p>
                </div>
              )}
            </>
          )}
        </Form.List>
      </div>
    </div>
  );
};

export default RewardForm;
